import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Sparkles, ClipboardPaste, Wand2, X, Loader2, Trash2,
  Plus, AlertTriangle, User, Calendar, Timer, FileText, StickyNote, Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const emptyItem = () => ({
  description: '',
  applicant: '',
  documents_required: '',
  date_requested: '',
  due_date: '',
  person_responsible: '',
  notes: '',
});

/**
 * Two-step Smart Import dialog:
 *   1. paste  → user pastes lender email, clicks Parse
 *   2. review → editable list of AI-parsed MIRs; user can edit, remove or add
 *               rows, then Save creates them all in one bulk POST.
 */
export default function MirSmartImportDialog({ open, onClose, pack, onPackChange }) {
  const { token } = useAuth();
  const auth = { Authorization: `Bearer ${token}` };

  const [step, setStep]         = useState('paste');   // paste | review
  const [emailText, setEmailText] = useState('');
  const [items, setItems]       = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [parsing, setParsing]   = useState(false);
  const [saving, setSaving]     = useState(false);

  const reset = () => {
    setStep('paste');
    setEmailText('');
    setItems([]);
    setWarnings([]);
  };

  const closeAll = () => {
    reset();
    onClose();
  };

  const doPaste = async () => {
    try {
      const t = await navigator.clipboard.readText();
      if (t) {
        setEmailText(t);
        toast.success('Pasted from clipboard');
      }
    } catch {
      toast.error('Clipboard access denied — paste manually with Ctrl+V');
    }
  };

  const parseEmail = async () => {
    if (emailText.trim().length < 20) {
      toast.error('Paste at least a couple of sentences from the lender email.');
      return;
    }
    setParsing(true);
    try {
      const res = await fetch(`${backendUrl}/api/lender-packs/${pack.id}/mirs/parse-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify({ email_text: emailText }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setItems((data.items || []).map(it => ({ ...emptyItem(), ...it })));
      setWarnings(data.warnings || []);
      if (!(data.items || []).length) {
        toast.error("Couldn't find any MIRs in that email. Try pasting a more complete request.");
      } else {
        toast.success(`Found ${data.items.length} MIR${data.items.length === 1 ? '' : 's'} — review before saving`);
        setStep('review');
      }
    } catch (e) {
      toast.error(`Parse failed — ${e.message}`);
    } finally {
      setParsing(false);
    }
  };

  const updateItem = (idx, patch) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, ...patch } : it));
  };
  const removeItem = (idx) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
  };
  const addItem = () => {
    setItems(prev => [...prev, {
      ...emptyItem(),
      date_requested: new Date().toISOString().slice(0, 10),
      person_responsible: pack.credit_team_member || pack.broker || '',
    }]);
  };

  const save = async () => {
    const clean = items
      .map(it => ({
        description:            (it.description || '').trim(),
        documents_required:     (it.documents_required || '').trim() || null,
        date_received:          it.date_requested || null,
        due_date:               it.due_date || null,
        person_responsible:     (it.person_responsible || '').trim() || null,
        waiting_on:             (it.applicant || '').trim() || null,
        internal_notes:         (it.notes || '').trim() || null,
      }))
      .filter(it => it.description);
    if (!clean.length) {
      toast.error('Every MIR needs a description');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${backendUrl}/api/lender-packs/${pack.id}/mirs/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify({ items: clean }),
      });
      if (!res.ok) throw new Error(await res.text());
      const updated = await res.json();
      onPackChange?.(updated);
      toast.success(`${clean.length} MIR${clean.length === 1 ? '' : 's'} added to the pack`);
      closeAll();
    } catch (e) {
      toast.error(`Save failed — ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && closeAll()}>
      <DialogContent
        className="max-w-3xl p-0 gap-0 overflow-hidden bg-white"
        style={{ colorScheme: 'light' }}
        data-testid="mir-smart-import-dialog"
      >
        {/* Header */}
        <DialogHeader className="p-0">
          <div
            className="px-5 py-3.5 flex items-start gap-3"
            style={{ background: 'linear-gradient(90deg, #eef2ff 0%, #e0e7ff 100%)', borderBottom: '1px solid #a5b4fc' }}
          >
            <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: '#4f46e5' }}>
              <Sparkles className="w-5 h-5" style={{ color: '#ffffff' }} />
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-base font-black leading-snug" style={{ color: '#312e81' }}>
                Smart MIR Import
              </DialogTitle>
              <DialogDescription className="text-[11px] font-semibold" style={{ color: '#4338ca' }}>
                {step === 'paste'
                  ? 'Paste the lender email — Hey Derwent will split it into individual MIRs.'
                  : `Reviewing ${items.length} MIR${items.length === 1 ? '' : 's'} — edit, remove or add before saving.`}
              </DialogDescription>
            </div>
            <button type="button" onClick={closeAll} className="p-1.5 rounded-md hover:bg-white/60" data-testid="mir-smart-close">
              <X className="w-4 h-4" style={{ color: '#4338ca' }} />
            </button>
          </div>
        </DialogHeader>

        {/* Body */}
        {step === 'paste' && (
          <div className="p-5 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <label className="text-[10px] font-black uppercase tracking-widest" style={{ color: '#4338ca' }}>
                Lender email
              </label>
              <button
                type="button"
                onClick={doPaste}
                className="inline-flex items-center gap-1 text-[10.5px] font-black uppercase tracking-wider px-2 py-1 rounded-md hover:bg-indigo-50"
                style={{ color: '#4338ca' }}
                data-testid="mir-smart-paste"
              >
                <ClipboardPaste className="w-3 h-3" /> Paste from clipboard
              </button>
            </div>
            <Textarea
              value={emailText}
              onChange={(e) => setEmailText(e.target.value)}
              placeholder="Paste the FULL lender email here — Hey Derwent will identify each request and split it into a separate MIR."
              rows={12}
              className="text-[13px] font-mono resize-none"
              style={{ color: '#0f172a' }}
              data-testid="mir-smart-email-input"
            />
            <div className="text-[11px] font-semibold" style={{ color: '#64748b' }}>
              Client on file: <span style={{ color: '#0f172a' }}>{pack.client_name}</span> · Lender: <span style={{ color: '#0f172a' }}>{pack.lender}</span>
            </div>
          </div>
        )}

        {step === 'review' && (
          <div className="p-4 max-h-[65vh] overflow-y-auto space-y-3">
            {warnings.length > 0 && (
              <div className="rounded-lg p-2.5 flex gap-2" style={{ background: '#fef3c7', border: '1px solid #fcd34d' }}>
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: '#92400e' }} />
                <div className="text-[11px] font-semibold leading-snug" style={{ color: '#78350f' }}>
                  <div className="text-[9.5px] font-black uppercase tracking-widest mb-1" style={{ color: '#92400e' }}>Hey Derwent notes</div>
                  {warnings.map((w, i) => (<div key={i}>· {w}</div>))}
                </div>
              </div>
            )}

            {items.map((it, idx) => (
              <MirDraftRow key={idx} item={it} index={idx + 1}
                onChange={(patch) => updateItem(idx, patch)}
                onRemove={() => removeItem(idx)} />
            ))}

            <button
              type="button"
              onClick={addItem}
              className="w-full inline-flex items-center justify-center gap-1.5 py-2 rounded-lg border-2 border-dashed text-[11px] font-black uppercase tracking-widest transition-colors hover:bg-indigo-50"
              style={{ borderColor: '#c7d2fe', color: '#4338ca' }}
              data-testid="mir-smart-add-row"
            >
              <Plus className="w-3.5 h-3.5" /> Add another MIR
            </button>
          </div>
        )}

        {/* Footer */}
        <div
          className="px-5 py-3 border-t flex items-center justify-between gap-2 flex-wrap"
          style={{ background: '#f8fafc', borderColor: '#e2e8f0' }}
        >
          {step === 'paste' ? (
            <>
              <Button variant="outline" size="sm" onClick={closeAll} className="text-xs" data-testid="mir-smart-cancel">
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={parseEmail}
                disabled={parsing || emailText.trim().length < 20}
                className="text-xs text-white bg-indigo-600 hover:bg-indigo-700"
                data-testid="mir-smart-parse"
              >
                {parsing ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Reading email…</>
                         : <><Wand2 className="w-3.5 h-3.5 mr-1" /> Parse email into MIRs</>}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={() => setStep('paste')} className="text-xs" data-testid="mir-smart-back">
                ← Back to email
              </Button>
              <Button
                size="sm"
                onClick={save}
                disabled={saving || items.length === 0}
                className="text-xs text-white bg-emerald-600 hover:bg-emerald-700"
                data-testid="mir-smart-save"
              >
                {saving ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Saving…</>
                        : <>Save {items.length} MIR{items.length === 1 ? '' : 's'} to pack</>}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MirDraftRow({ item, index, onChange, onRemove }) {
  return (
    <div
      className="rounded-lg border-2 p-3 relative"
      style={{ borderColor: '#e2e8f0', background: '#ffffff', colorScheme: 'light' }}
      data-testid={`mir-smart-row-${index}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-5 h-5 rounded-md text-[10px] font-black tabular-nums" style={{ background: '#4f46e5', color: '#ffffff' }}>
            #{index}
          </span>
          <span className="text-[9.5px] font-black uppercase tracking-widest" style={{ color: '#4338ca' }}>
            Draft MIR
          </span>
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="p-1 rounded hover:bg-rose-50"
          title="Remove"
          data-testid={`mir-smart-remove-${index}`}
        >
          <Trash2 className="w-3.5 h-3.5" style={{ color: '#e11d48' }} />
        </button>
      </div>

      <MicroLabel icon={FileText} label="What the lender asked for">
        <Textarea value={item.description || ''}
          onChange={(e) => onChange({ description: e.target.value })}
          rows={2}
          placeholder="E.g. Updated payslips (two most recent)"
          className="text-xs" style={{ color: '#0f172a' }}
          data-testid={`mir-smart-desc-${index}`} />
      </MicroLabel>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
        <MicroLabel icon={Users} label="Applicant / relates to">
          <Input value={item.applicant || ''}
            onChange={(e) => onChange({ applicant: e.target.value })}
            placeholder="e.g. John Smith / All applicants"
            className="h-8 text-xs" style={{ color: '#0f172a' }}
            data-testid={`mir-smart-applicant-${index}`} />
        </MicroLabel>
        <MicroLabel icon={User} label="Person responsible">
          <Input value={item.person_responsible || ''}
            onChange={(e) => onChange({ person_responsible: e.target.value })}
            placeholder="Broker / Credit officer"
            className="h-8 text-xs" style={{ color: '#0f172a' }}
            data-testid={`mir-smart-responsible-${index}`} />
        </MicroLabel>
      </div>

      <MicroLabel icon={FileText} label="Documents / info required" className="mt-2">
        <Textarea value={item.documents_required || ''}
          onChange={(e) => onChange({ documents_required: e.target.value })}
          rows={2}
          placeholder="e.g. Two most recent payslips (last 6 weeks)"
          className="text-xs" style={{ color: '#0f172a' }}
          data-testid={`mir-smart-docs-${index}`} />
      </MicroLabel>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
        <MicroLabel icon={Calendar} label="Date requested">
          <Input type="date" value={item.date_requested || ''}
            onChange={(e) => onChange({ date_requested: e.target.value })}
            className="h-8 text-xs" style={{ color: '#0f172a' }}
            data-testid={`mir-smart-date-${index}`} />
        </MicroLabel>
        <MicroLabel icon={Timer} label="Due date">
          <Input type="date" value={item.due_date || ''}
            onChange={(e) => onChange({ due_date: e.target.value })}
            className="h-8 text-xs" style={{ color: '#0f172a' }}
            data-testid={`mir-smart-due-${index}`} />
        </MicroLabel>
      </div>

      {(item.notes || item.notes === '') && (
        <MicroLabel icon={StickyNote} label="Lender notes / instructions" className="mt-2">
          <Textarea value={item.notes || ''}
            onChange={(e) => onChange({ notes: e.target.value })}
            rows={1}
            placeholder="Any extra context from the lender…"
            className="text-xs" style={{ color: '#0f172a' }}
            data-testid={`mir-smart-notes-${index}`} />
        </MicroLabel>
      )}
    </div>
  );
}

function MicroLabel({ icon: Icon, label, className = '', children }) {
  return (
    <div className={className}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3 h-3" style={{ color: '#64748b' }} />
        <span className="text-[9.5px] font-black uppercase tracking-widest" style={{ color: '#64748b' }}>{label}</span>
      </div>
      {children}
    </div>
  );
}
